import styled from "@emotion/styled";
import { StyledLightboxContainer, StyledLightboxToolbar } from "../shared-lightbox.styles";
import { MDSMediaQueries } from "@/mds/foundations";
import { MDSButton } from "@/mds/components/buttons";

/**
 * Overrides the shared container so the PDF content fills the entire viewport.
 * The bottom padding that normally reserves space for the toolbar is removed;
 * the toolbar and carousel now float on top via absolute positioning.
 */
export const StyledPdfLightboxContainer = styled(StyledLightboxContainer)`
  padding: 0;
`;

/**
 * Wrapper for floating overlay elements (toolbar + carousel).
 * Fades in/out via opacity transition for the auto-hide behavior.
 */
export const StyledOverlayLayer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;

  & > * {
    pointer-events: auto;
  }
`;

/** Floating toolbar positioned at the bottom center of the viewport. */
export const StyledPdfToolbar = styled(StyledLightboxToolbar)``;

/** Floating carousel positioned above the toolbar. */
export const StyledPdfCarouselWrapper = styled.div`
  position: absolute;
  bottom: calc(68px + env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
`;

/** Zoom reset button with hover color matching the adjacent MDSIconButtons. */
export const StyledZoomResetButton = styled(MDSButton)`
  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.hoverOverlay.default};
  }
`;

/** Page indicator wrapper that shrinks on small screens to prevent line-wrapping. */
export const StyledPageIndicator = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: ${({ theme }) => theme.spacing["10"]};
  padding: 0 ${({ theme }) => theme.spacing["1"]};
  user-select: none;
  white-space: nowrap;

  ${MDSMediaQueries.rules.phone} {
    & > * {
      font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
    }
  }
`;

export const StyledPageIndicatorButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: inline-flex;
  align-items: center;
  font: inherit;
  line-height: inherit;
  color: inherit;

  &:disabled {
    cursor: default;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.hoverOverlay.default};
  }
`;

export const StyledPageIndicatorValue = styled.span`
  display: inline-grid;
  grid-template-columns:
    var(--pdf-lightbox-page-number-width, 2ch)
    auto
    var(--pdf-lightbox-page-number-width, 2ch);
  align-items: center;
  column-gap: 0;
  line-height: inherit;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
`;

export const StyledPageCurrent = styled.span`
  text-align: right;
`;

export const StyledPageSeparator = styled.span`
  text-align: center;
  padding: 0 1px;
`;

export const StyledPageTotal = styled.span`
  text-align: left;
`;

/** Editable page number input styled to look like a label until focused. */
export const StyledPageInput = styled.input`
  width: var(--pdf-lightbox-page-number-width, 2ch);
  box-sizing: border-box;
  border: 0;
  border-radius: 0;
  background: transparent;
  text-align: right;
  font: inherit;
  font-size: inherit;
  font-family: inherit;
  font-weight: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  color: inherit;
  padding: 0;
  margin: 0;
  outline: none;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  caret-color: currentColor;

  &:focus {
    border: 0;
    background: transparent;
    outline: none;
  }

  /* Hide spinner arrows in number-like inputs */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

export const StyledPdfContentArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const StyledPdfLoadingState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.graphite[0]};
`;

export const StyledPdfErrorState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.graphite[0]};
  font-size: ${({ theme }) => theme.fontSizes["header-l"]};
  line-height: ${({ theme }) => theme.lineHeights["header-l"]};
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

export const StyledPdfScrollContainer = styled.div<{ isZoomed: boolean }>`
  overflow: ${({ isZoomed }) => (isZoomed ? "auto" : "hidden")};
  width: 100%;
  flex: 1;
  min-height: 0;

  /* Center the page when it's smaller than the container via margin auto;
     flexbox centering would clip the overflow on the start edges. */
  & > div {
    margin: auto;
    width: fit-content;
  }
`;

/**
 * Wrapper that applies CSS transform zoom. The sizer has explicit dimensions
 * (naturalWidth × zoom, naturalHeight × zoom) so the scroll container gets
 * real scrollbars, while the inner content is just visually scaled via
 * transform: scale(). This avoids re-rendering the PDF canvas on every zoom.
 *
 * Before page dimensions are measured (pageWidth/pageHeight = 0), the wrapper
 * uses auto sizing so the canvas can render naturally for initial measurement.
 */
export const StyledPdfZoomWrapper = styled.div<{
  zoom: number;
  pageWidth: number;
  pageHeight: number;
}>`
  ${({ pageWidth, pageHeight, zoom }) =>
    pageWidth > 0 && pageHeight > 0
      ? `
    width: ${pageWidth * zoom}px;
    height: ${pageHeight * zoom}px;
  `
      : ""}

  & > div {
    ${({ pageWidth, pageHeight, zoom }) =>
      pageWidth > 0 && pageHeight > 0
        ? `
      transform: scale(${zoom});
      transform-origin: 0 0;
    `
        : ""}
  }

  .react-pdf__Page {
    background: none !important;
  }

  mark.pdf-match,
  mark.pdf-match-current {
    font: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    color: transparent;
  }

  mark.pdf-match {
    background: ${({ theme }) => theme.colors.background.findHighlight};
  }

  mark.pdf-match-current {
    background: ${({ theme }) => theme.colors.background.findHighlightCurrent};
  }
`;
